import React, { useCallback, useState } from 'react';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import { NuvoTabView } from '@nuvolo/nuux-core/components/NuvoTabView';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import { Item } from 'devextreme-react/tabs';
import { useNavigate } from 'react-router-dom';
import { isFormDirty, setFormDirty } from '../Form/dirtyFormTracker';
import useToast from '#/common/hooks/useToast';
import { color } from '@nuvolo/nuux-theme/utils';
import { isDarkTheme } from '#/common/utils/themeDetection';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import type SubPageProps from './SubPageProps';
import Scroller from './Scroller';
import If from '../If';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import Dot from '../Dot';
import PoweredBy from '../PoweredBy';
import styled from 'styled-components';
import WorkingOnItThrobber from '#/app/components/Throbbers/WorkingOnItThrobber';
import MobileSwitch from '../MobileSwitch';
import { PageType } from './SubPageProps';
import PageActionsContainer from './PageActions/PageActionsContainer';
import PageProvider from '#/app/providers/PageProvider/PageProvider';
import TMMButton from '../TMMButton';
import SelectModeContext from '#/app/providers/SelectModeProvider/SelectModeContext';

const StyledNuvoBoxWrapper = styled.div`
  position: relative;
  width: 100%;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 40px;
    ${({ theme }) => {
      if (isDarkTheme(theme)) {
        return `background: linear-gradient(90deg, rgba(255, 0, 0, 0) 50%, rgba(13, 15, 18, 1) 100%);`;
      }
      return `background: linear-gradient(90deg, rgba(255, 255, 255, 0) 50%, rgba(255, 255, 255, 1) 100%);`;
    }}
    pointer-events: none;
    z-index: 1;
  }
`;

const StyledNuvoBox = styled(NuvoBox)`
  overflow: auto;
  scrollbar-width: none;
`;

const ContentWrapper = styled.div<{ $isDesktop: boolean; $isForm: boolean }>`
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1 1 0%;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  padding: 0 24px;
  min-width: 0;
  ${({ $isDesktop, $isForm }) =>
    $isDesktop || $isForm
      ? `
    height: 100%;
    max-height: 100%;
  `
      : ''}
`;

const HeaderWrapper = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
`;

const HeaderRow = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 16px;
  margin-bottom: 8px;
  gap: 8px;
  min-width: 0;
`;

const TitleContainer = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const StyledNuvoTabView = styled(NuvoTabView)`
  .dx-tabs-wrapper {
    height: 40px !important;
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    align-content: center !important;

    .dx-tab {
      border-radius: 0 !important;
      background-color: transparent !important;
      color: ${color('custom', 'tabGray')};
      font-weight: 400;
      font-size: 16px;
      margin: 0 16px 0 0 !important;
      padding: 0 !important;
      height: 32px !important;
      width: fit-content;
      height: 32px !important;
      display: flex !important;
      flex-direction: row !important;
      align-items: center !important;
      align-content: center !important;
      border-bottom: 3px solid transparent !important;
      transition: all 0.23s ease-in-out !important;
    }

    .dx-tab-content {
      margin: 0 !important;
      padding: 0 5px !important;
      width: fit-content;
      height: 32px !important;
      display: flex !important;

      flex-direction: row !important;
      align-items: center !important;
      align-content: center !important;

      div {
        display: flex !important;
        flex-direction: row !important;
        align-items: center !important;
        align-content: center !important;
      }
    }

    .dx-tab-selected > div {
      color: ${color('accent', 'middle')} !important;
      transition: color 0.23s;
    }

    .dx-state-hover,
    .dx-tab-selected,
    .dx-state-active {
      border-bottom: 3px solid ${color('accent', 'middle')} !important;
      transition: color 0.23s;
    }

    .dx-tab-content > div > div {
      border-radius: 20px;
      padding: 4px 10px;
      background-color: ${color('custom', 'tabsCounterColor')};
      height: 24px;

      display: flex !important;
      align-items: center !important;
      align-content: center !important;

      div {
        color: ${color('custom', 'badgeGray')};
        padding: 0 !important;
      }
    }
  }
`;

const StyledFormWrapper = styled.div<{ isForm: boolean; hasTabs: boolean }>`
  ${(props) =>
    !props.isForm &&
    `
  overflow-y: hidden;
  height: 100%;
  `}

  ${(props) =>
    props.isForm &&
    `
  overflow-y: scroll;
  padding-top: 16px;
  `}

  ${(props) =>
    props.isForm &&
    props.hasTabs &&
    `
  height: calc(100vh - 170px)
  `}

  ${(props) =>
    props.isForm &&
    !props.hasTabs &&
    `
  height: calc(100vh - 120px)
  `}
`;

const TruncatedTextWrapper = styled.div<{ pageType?: PageType }>`
  h2 {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const SubPage: React.FC<SubPageProps> = (props) => {
  const {
    hideTitle,
    title,
    isLoading = false,
    children,
    tabs,
    type,
    selectedTab: tabKey,
    disabledBack: disableBack = false,
    showGoBackButton = false,
    goBackByHowManyPages = 1,
    hideSelectToggle = false,
  } = props;

  useDocumentTitle(title);
  const { isDesktop } = useViewport();

  const selectedTabIndex = tabs?.findIndex((t) => t.key === tabKey) ?? 0;

  const navigate = useNavigate();
  const { confirm } = useToast();

  const [isSelectMode, setIsSelectMode] = useState(false);

  const selectTab = useCallback(
    (index: number) => {
      const tab = tabs?.[index];
      if (tab) {
        navigate(`../${tab.path ?? tab.key}`);
      }
    },
    [navigate, tabs],
  );

  const goBack = useCallback(async () => {
    if (isFormDirty()) {
      const leave = await confirm('warning', 'Leave page? Changes you made may not be saved.', [
        'Cancel',
        'Leave',
      ]);
      if (!leave) return;
      setFormDirty(false);
    }
    navigate(-goBackByHowManyPages);
  }, [navigate, confirm, goBackByHowManyPages]);

  const selectedTab = tabs?.length && selectedTabIndex != -1 ? tabs[selectedTabIndex] : null;
  const isForm = selectedTab?.type === 'form' || type === 'form';

  const SurroundingComponent = Scroller;
  const hasTabs = !!(tabs && tabs.length > 0);

  // Determine CSS class for Scroller based on type
  const scrollerClassName = `tmm-page tmm-page-${type ?? 'other'}`;

  const propsForSurroundingComponent = { className: scrollerClassName };

  const hasToggle = type === 'list' && !hideSelectToggle;

  return (
    <PageProvider>
      <SelectModeContext.Provider value={{ selectModeOn: isSelectMode }}>
        <SurroundingComponent {...propsForSurroundingComponent}>
          <ContentWrapper $isDesktop={isDesktop} $isForm={isForm}>
            <If condition={!hideTitle && !!title}>
              <HeaderWrapper>
                <HeaderRow>
                  <NuvoBox flexShrink={0}>
                    {showGoBackButton && (
                      <TMMButton
                        icon="angle-left"
                        type="secondary"
                        disabled={disableBack}
                        onClick={goBack}
                      />
                    )}
                  </NuvoBox>
                  <TitleContainer>
                    <TruncatedTextWrapper pageType={type ?? 'list'}>
                      <NuvoText className="bold" type="header2">
                        {title}
                      </NuvoText>
                    </TruncatedTextWrapper>
                  </TitleContainer>
                  <PageActionsContainer />
                </HeaderRow>
                <If condition={!isDesktop && !tabs?.length && hasToggle}>
                  <MobileSwitch
                    margin="16px 16px 8px 16px"
                    value={isSelectMode}
                    onValueChange={setIsSelectMode}
                  />
                </If>
              </HeaderWrapper>
            </If>
            <If condition={!!tabs && tabs.length > 0}>
              <NuvoBox display="flex">
                <StyledNuvoBoxWrapper style={{ width: hasToggle ? 'calc(100% - 100px)' : '100%' }}>
                  <StyledNuvoBox>
                    <StyledNuvoTabView
                      type="secondary"
                      selectedIndex={selectedTabIndex}
                      className="subpage-tabs"
                      onSelectedIndexChange={selectTab}>
                      {tabs?.map((t) => (
                        <Item key={t.key}>
                          <NuvoBox
                            display="flex"
                            flexDirection="row"
                            gap="7px"
                            backgroundColor="transparent">
                            {t.text}
                            {t.count !== undefined && (
                              <Dot size="auto" color={color('ui', 'dark')}>
                                <NuvoBox paddingX="5px">
                                  <NuvoText type="small" color={color('contrast', 'light')}>
                                    {t.count < 1000 ? t.count : '999+'}
                                  </NuvoText>
                                </NuvoBox>
                              </Dot>
                            )}
                          </NuvoBox>
                        </Item>
                      ))}
                    </StyledNuvoTabView>
                  </StyledNuvoBox>
                </StyledNuvoBoxWrapper>
                <If condition={!isDesktop && hasToggle}>
                  <MobileSwitch value={isSelectMode} onValueChange={setIsSelectMode} />
                </If>
              </NuvoBox>
            </If>
            <NuvoBox flex="1" marginTop={isForm && !hasTabs ? '20px' : undefined}>
              {isLoading ? (
                <WorkingOnItThrobber />
              ) : (
                <StyledFormWrapper isForm={isForm} hasTabs={hasTabs}>
                  {children}
                  {isDesktop && isForm && (
                    <NuvoBox display="flex" justifyContent="flex-end">
                      <PoweredBy />
                    </NuvoBox>
                  )}
                </StyledFormWrapper>
              )}
            </NuvoBox>
            {isDesktop && !isForm && (
              <NuvoBox style={{ alignSelf: 'flex-end' }}>
                <PoweredBy />
              </NuvoBox>
            )}
          </ContentWrapper>
        </SurroundingComponent>
      </SelectModeContext.Provider>
    </PageProvider>
  );
};

export default React.memo(SubPage);
